import {
  Box,
  Button,
  ClickAwayListener,
  Grow,
  ListItemText,
  makeStyles,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  PopperPlacementType,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { BorderWidthWithColor } from '../../../../core/config/mainTheme';
import { useErrorsDispatch } from '../../../../pages/FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../pages/FinancialData/shared/ErrorContext/ErrorContext.types';
import ConfirmationDialog from '../../../ConfirmationDialog';
import { ActionLink } from './ActionBox.type';

export interface ActionBoxProps {
  buttonTitle?: string;
  actionItems?: ActionLink[];
  placement?: PopperPlacementType;
  externalClasses?: Partial<ReturnType<typeof useStyles>>;
  boxVariant?: 'text' | 'outlined' | 'contained';
}

const ActionBox: React.FC<ActionBoxProps> = ({
  buttonTitle,
  actionItems,
  placement = 'bottom-end',
  externalClasses,
  boxVariant = 'contained',
}) => {
  const classes = { ...useStyles(), ...externalClasses };
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { t } = useTranslation();
  const [openConfDialog, setConfDialog] = useState(false);
  const [confText, setConfText] = useState('app:confirmationText:defaultText');
  const [currentItem, setCurrentItem] = useState<ActionLink>();
  const [customDialog, setCustomDialog] = useState<React.ReactNode>();
  const dispatchErrors = useErrorsDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleClick = () => {
    setOpen(!open);
  };

  const handleClose = (event: React.MouseEvent<EventTarget>) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const hanldeOpenConfirmation = (item?: any) => {
    setConfText(`app:confirmationText:${item.title}`);
    setCurrentItem(item);
    setConfDialog(true);
  };

  const handleCloseConfirmation = () => {
    setConfDialog(false);
  };

  const handleOkConfirmation = async (onClick?: () => Promise<void>) => {
    setConfDialog(false);

    let infoSnack;
    if (currentItem?.snackbarReportText) {
      infoSnack = enqueueSnackbar(<Trans i18nKey={currentItem.snackbarReportText} />, {
        variant: 'info',
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }
    if (currentItem && onClick) {
      try {
        await onClick();
      } catch (error) {
        if (infoSnack) {
          closeSnackbar(infoSnack);
        }
        dispatchErrors({
          type: ErrorContextActionTypes.SetError,
          payload: {
            error,
            count: error?.data?.detail,
          },
        });
      }
    }
  };

  useEffect(() => {
    if (currentItem?.customDialog) {
      setCustomDialog(
        currentItem?.customDialog(openConfDialog, handleCloseConfirmation, handleOkConfirmation),
      );
    } else {
      setCustomDialog(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentItem, openConfDialog]);

  return (
    <Box className={`${classes.rightBox}`}>
      <Button
        className={classes.button}
        aria-controls="customized-menu"
        ref={anchorRef}
        aria-haspopup="true"
        variant={boxVariant}
        color="primary"
        onClick={handleClick}
      >
        {buttonTitle}
        <ArrowDropDownIcon />
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        transition
        disablePortal
        placement={placement}
        className={classes.menuBox}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem={open} id="menu-list-grow" className={classes.menuItems}>
                  {actionItems
                    ? actionItems.map(
                        (item: ActionLink, index: number) =>
                          item &&
                          item.title &&
                          !item.isHidden && (
                            <MenuItem
                              onClick={() => hanldeOpenConfirmation(item)}
                              key={index}
                              disabled={item?.count === 0 || item.isNotActive}
                            >
                              <ListItemText
                                primary={`${t(item.title)} ${
                                  item?.count || item.count === 0 ? `(${item.count})` : ''
                                }`}
                              />
                            </MenuItem>
                          ),
                      )
                    : null}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {customDialog ? (
        customDialog
      ) : (
        <ConfirmationDialog
          open={openConfDialog}
          handleClose={handleCloseConfirmation}
          handleConfirmOk={() => handleOkConfirmation(currentItem?.onClick)}
          text={confText}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  menuBox: {
    zIndex: 2,
    borderTop: BorderWidthWithColor(1, theme.palette.primary.contrastText),
  },
  rightBox: {
    marginLeft: 'auto',
    position: 'relative',
    display: 'flex',
  },
  button: {
    background: theme.palette.primary.dark,
    borderRight: `1px solid ${theme.palette.background.paper}`,
    borderLeft: `1px solid ${theme.palette.background.paper}`,
    borderRadius: 0,
    '& svg': {
      marginLeft: '10px',
    },
  },
  menuItems: {
    background: theme.palette.primary.dark,
    color: theme.palette.background.paper,
  },
  margin: {
    margin: theme.spacing(1),
  },
}));

export default ActionBox;
